import * as SliderPrimitive from "@radix-ui/react-slider";
import { RotateCcw } from "lucide-react";
import { formatPrice } from "../lib/company";
import { tv, useT } from "../lib/i18n";

const FilterLabel = ({ children }) => <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--muted)] mb-3">{children}</p>;
const RangeThumb = ({ testid }) => (
  <SliderPrimitive.Thumb data-testid={testid} aria-label={testid}
    className="block w-5 h-5 rounded-full bg-white border-2 border-[var(--ink)] shadow transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--red)]" />
);

export const priceBounds = (cars) => {
  const p = cars.map((c) => c.prezzo).filter((v) => typeof v === "number");
  if (!p.length) return [0, 0];
  return [Math.floor(Math.min(...p) / 1000) * 1000, Math.ceil(Math.max(...p) / 1000) * 1000];
};

export const InventoryFilters = ({ cars, filters, setFilters, count, onReset, active }) => {
  const t = useT();
  const fuels = [...new Map(cars.filter((c) => c.carburante?.trim()).map((c) => [c.carburante.trim().toLowerCase(), c.carburante.trim()])).entries()];
  const years = [...new Set(cars.map((c) => c.anno).filter(Boolean))].sort((a, b) => b - a);
  const [min, max] = priceBounds(cars);
  const price = filters.price ?? [min, max];
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const chip = (on) => `rounded-full px-4 h-9 text-xs font-semibold border transition-colors ${on ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "bg-white border-[var(--line)] text-[var(--ink-2)] hover:border-[var(--ink)]"}`;

  return (
    <div className="mb-12 rounded-[24px] border border-[var(--line)] bg-white/70 backdrop-blur p-5 md:p-7 grid gap-7 md:grid-cols-12 md:items-end" data-testid="inventory-filters">
      <div className="md:col-span-5">
        <FilterLabel>{t.inv.fuel}</FilterLabel>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => set("fuel", "")} className={chip(!filters.fuel)} data-testid="filter-fuel-all">{t.inv.all}</button>
          {fuels.map(([k, v]) => (
            <button key={k} onClick={() => set("fuel", k)} className={chip(filters.fuel === k)} data-testid={`filter-fuel-${k}`}>{tv(t, v)}</button>
          ))}
        </div>
      </div>
      <div className="md:col-span-4">
        <div className="flex justify-between items-baseline">
          <FilterLabel>{t.inv.price}</FilterLabel>
          <p className="text-xs font-semibold mb-3" data-testid="filter-price-value">{formatPrice(price[0])} – {formatPrice(price[1])}</p>
        </div>
        <SliderPrimitive.Root min={min} max={max} step={500} value={price} disabled={min === max} minStepsBetweenThumbs={1}
          onValueChange={(v) => set("price", v)} className="relative flex items-center w-full h-9 touch-none select-none" data-testid="filter-price-slider">
          <SliderPrimitive.Track className="relative h-1 grow rounded-full bg-[var(--line)]">
            <SliderPrimitive.Range className="absolute h-full rounded-full bg-[var(--red)]" />
          </SliderPrimitive.Track>
          <RangeThumb testid="filter-price-min" />
          <RangeThumb testid="filter-price-max" />
        </SliderPrimitive.Root>
      </div>
      <div className="md:col-span-3 flex gap-3 items-end">
        <div className="flex-1">
          <FilterLabel>{t.inv.year}</FilterLabel>
          <select value={filters.year} onChange={(e) => set("year", e.target.value)} data-testid="filter-year-select"
            className="w-full h-9 rounded-full border border-[var(--line)] bg-white px-4 text-xs font-semibold outline-none focus:border-[var(--ink)]">
            <option value="">{t.inv.anyYear}</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        {active && (
          <button onClick={onReset} data-testid="filter-reset-btn" aria-label={t.inv.reset} title={t.inv.reset}
            className="grid place-items-center w-9 h-9 shrink-0 rounded-full border border-[var(--line)] hover:bg-[var(--ink)] hover:text-white transition-colors">
            <RotateCcw className="w-4 h-4" />
          </button>
        )}
      </div>
      <p className="md:col-span-12 -mt-2 text-sm text-[var(--muted)]" data-testid="filter-results-count">{t.inv.results(count)}</p>
    </div>
  );
};
